import {isFilled} from '@prismicio/client'
import {Container} from 'src/common/Container'
import {useLinkResolver} from 'prismicio'
import cn from 'classnames'
import {getButtonsData} from 'src/common/helpers'
import {ButtonDocument, CtaV2SliceDefault, CtaV2SliceWithoutImage} from 'prismicio-types'
import {PrismicNextImage} from '@prismicio/next'
import {ButtonV2} from 'src/common/ButtonV2'
import {StarRatings} from 'slices/HeroV2/RatingsSection/StarRatings'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'

/**
 * Props for `CtaV2`.
 */
export type CtaV2Props = {
  slice: CtaV2SliceDefault | CtaV2SliceWithoutImage
  pageUid: string
  componentIndex: number
  buttonsData?: ButtonDocument[]
}

/**
 * Component for "CtaV2" Slices.
 */
export const OldVariations = ({
  slice: {primary},
  slice,
  pageUid,
  buttonsData,
  componentIndex,
}: CtaV2Props): JSX.Element => {
  const {linkResolver} = useLinkResolver()
  const sanitizedHeading = sanitizeHtml(primary.title || '')

  const {primaryButtonData, secondaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: primary.primary_button,
    secondaryButton: primary.secondary_button,
  })

  return (
    <Container
      backgroundColor={primary.section_background_color || 'Grey/100'}
      className="!py-12 md:!py-15"
      data-slice-variation={slice.variation}
    >
      <div
        className="flex w-full max-w-[1200px] gap-4 px-5 max-md:flex-col md:gap-3 md:px-10 lg:px-5 xl:px-0"
        data-aos="fade-up"
        data-aos-once
      >
        <div
          className={cn('flex w-full flex-col justify-center gap-6 rounded-lg bg-green-dark p-6 lg:p-15', {
            'items-center': slice.variation === 'withoutImage',
          })}
        >
          <div className="flex h-full flex-col gap-6">
            {slice.items.length > 0 ? (
              <div className="w-fit">
                <StarRatings review={slice.items[0]} color="Neon/500" isActive darkBackground />
              </div>
            ) : null}
            <h4
              className={cn('max-w-[550px] text-24 font-medium leading-8 text-moss-white md:text-32 md:leading-10', {
                'max-w-[588px]': !primary.enable_full_width,
                'text-center': slice.variation === 'withoutImage',
              })}
              dangerouslySetInnerHTML={{__html: sanitizedHeading}}
            />
          </div>
          <div className="flex gap-3 max-md:flex-col md:flex-wrap">
            <ButtonV2
              variant="primary"
              href={linkResolver(primaryButtonData?.link || primary.primary_button_url, 'primary-button-cta')}
              trackingData={{
                mktgId: primaryButtonData?.marketing_id || primary.primary_button_mktg_id || 'cta-button',
                location: `component-${componentIndex}-cta`,
                dataType: 'primary-button',
                pageName: pageUid,
                label: primaryButtonData?.label || primary.primary_button_label || '',
              }}
              id={`component-${componentIndex}-cta-1`}
              className="w-fit"
            >
              {primaryButtonData?.label || primary.primary_button_label}
            </ButtonV2>
            {(isFilled.keyText(primary.secondary_button_label) || secondaryButtonData) && (
              <ButtonV2
                variant="secondary"
                href={linkResolver(secondaryButtonData?.link || primary.secondary_button_url, 'secondary-button-cta')}
                trackingData={{
                  mktgId:
                    secondaryButtonData?.marketing_id || primary.secondary_button_mktg_id || 'secondary-cta-button',
                  location: `component-${componentIndex}-cta`,
                  dataType: secondaryButtonData?.intent || 'cta-button',
                  pageName: pageUid,
                  label: secondaryButtonData?.label || primary.secondary_button_label || '',
                }}
                id={`component-${componentIndex}-cta-2`}
                className="w-fit"
              >
                {secondaryButtonData?.label || primary.secondary_button_label}
              </ButtonV2>
            )}
          </div>
        </div>
        {slice.variation === 'default' && (
          <div className="w-full max-md:aspect-[335/237.35] md:max-w-[384px]">
            <PrismicNextImage field={slice.primary.image} className="w-full rounded-lg max-md:object-cover md:h-full" />
          </div>
        )}
      </div>
    </Container>
  )
}
